import { COOKIE_SCRIPT_URL } from '@mopla/constants';
import { Logger } from '@mopla/logger';

/**
 * Injects the CookieScript consent banner and reports its decisions.
 *
 * Native only — on web the banner is injected by the GTM container, which the
 * apps do not load. The two setups are configured independently: when the
 * CookieScript tag in the container is pointed at a different banner,
 * COOKIE_SCRIPT_ID has to be updated to match.
 *
 * On web CookieScript hands its decision to gtag itself. Natively there is no
 * consumer for it, so the accepted categories (e.g. `performance`,
 * `targeting`) are passed to `onConsent` instead. This module only knows
 * CookieScript; what a category means for a tracking SDK is up to the caller.
 *
 * `onConsent` is called
 * - at app start, as soon as the banner has loaded (`CookieScriptLoaded`):
 *   with the decision stored from a previous start, or with no categories
 *   when none is stored (first start, consent expired or reset in the
 *   CookieScript dashboard). The SDK keeps the last consent and user id
 *   across starts, so a missing decision has to be reported as a denial.
 * - whenever the user decides in the banner (`CookieScriptAccept` /
 *   `CookieScriptAcceptAll` / `CookieScriptReject`). That covers the first
 *   decision as well as later changes, as the banner can be reopened via the
 *   "cookie settings" button in the profile (ProfileManager / InitialScreen).
 * The listeners stay registered for the lifetime of the app.
 *
 * `onReady` is called once, when the stored decision is known and has been
 * applied — so the caller can hold back tracking until then (see
 * initUserTracking). That is when the banner has loaded, or after
 * BANNER_TIMEOUT_MS at the latest, in case the banner never loads (offline,
 * CDN unreachable). Tracking then starts with the native defaults (denied),
 * in case of first startup; or the last stored decision, which the SDK keeps.
 * A decision arriving later is still reported via `onConsent`.
 */

export type TConsentCategories = string[];

interface ICookieScriptState {
	action?: 'accept' | 'reject';
	categories?: TConsentCategories;
}

interface ICookieScriptWindow {
	CookieScript?: {
		instance?: { currentState?: () => ICookieScriptState };
	};
}

const LOADED_EVENT = 'CookieScriptLoaded';

const DECISION_EVENTS = [
	'CookieScriptAccept',
	'CookieScriptAcceptAll',
	'CookieScriptReject',
];

const BANNER_TIMEOUT_MS = 5000;

const readState = (): ICookieScriptState =>
	(
		window as unknown as ICookieScriptWindow
	).CookieScript?.instance?.currentState?.() ?? {};

export const loadCookieScript = (
	onConsent: (categories: TConsentCategories) => void | Promise<void>,
	onReady: () => void
): void => {
	const logger = Logger.getInstance('UserTracking');

	const report = (eventName: string) => {
		const categories = readState().categories ?? [];
		logger.debug('CookieScript: consent reported', { eventName, categories });
		return onConsent(categories);
	};

	let isReady = false;

	const markReady = (reason: string) => {
		if (isReady) return;
		isReady = true;
		logger.debug('CookieScript: ready', { reason });
		onReady();
	};

	// Not cleared on load — once ready, it simply does nothing.
	setTimeout(() => {
		if (isReady) return;
		logger.error('CookieScript: banner did not load in time', {
			timeoutMs: BANNER_TIMEOUT_MS,
		});
		markReady('timeout');
	}, BANNER_TIMEOUT_MS);

	// Without a stored decision `action` is not set yet — deny until the user
	// decides via one of the DECISION_EVENTS.
	const onLoaded = async () => {
		try {
			if (readState().action) {
				await report(LOADED_EVENT);
			} else {
				logger.debug('CookieScript: no stored decision, denying');
				await onConsent([]);
			}
		} catch (err) {
			logger.error('CookieScript: applying stored consent failed', err);
		}
		markReady(LOADED_EVENT);
	};

	const onDecision = (event: Event) => report(event.type);

	// Listeners go first: `CookieScriptLoaded` may fire as soon as the script
	// has run, and a returning user's stored decision would be missed.
	window.addEventListener(LOADED_EVENT, onLoaded);
	for (const name of DECISION_EVENTS) {
		window.addEventListener(name, onDecision);
	}

	const script = document.createElement('script');

	script.src = COOKIE_SCRIPT_URL;
	script.async = true;
	script.setAttribute('charset', 'UTF-8');

	document.head.appendChild(script);

	logger.debug('CookieScript: banner injected', {
		src: COOKIE_SCRIPT_URL,
	});
};
